import { useAuth } from "@/_core/hooks/useAuth";
import { SitePageShell } from "@/components/SiteHeader";
import { Card, CardContent } from "@/components/ui/card";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { ORDER_STATUS_FLOW, ORDER_STATUS_LABEL } from "@shared/orderStatus";
import { formatKRW } from "@shared/pricing";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { Link, useRoute } from "wouter";
import type { OrderStatus } from "../../../drizzle/schema";

export default function OrderDetailPage() {
  const [, params] = useRoute<{ id: string }>("/orders/:id");
  const { isAuthenticated, loading } = useAuth();
  const id = Number(params?.id ?? 0);

  const detail = trpc.orders.detail.useQuery(
    { id },
    { enabled: isAuthenticated && id > 0 },
  );

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) window.location.href = getLoginUrl();
  }, [loading, isAuthenticated]);

  if (loading || !isAuthenticated || detail.isLoading) {
    return (
      <SitePageShell>
        <div className="container py-20 flex justify-center">
          <Loader2 className="animate-spin text-muted-foreground" />
        </div>
      </SitePageShell>
    );
  }

  if (!detail.data) {
    return (
      <SitePageShell>
        <div className="container py-20 text-center text-muted-foreground">
          주문 정보를 찾을 수 없습니다.{" "}
          <Link href="/my" className="text-primary underline ml-2">
            돌아가기
          </Link>
        </div>
      </SitePageShell>
    );
  }

  const { order, items, payments, shipping } = detail.data;
  const currentIdx = ORDER_STATUS_FLOW.indexOf(order.status as OrderStatus);

  return (
    <SitePageShell>
      <div className="container py-10 md:py-14 max-w-4xl">
        <Link
          href="/my"
          className="text-sm text-muted-foreground hover:text-primary"
        >
          ← 주문 목록으로
        </Link>
        <div className="mt-3 mb-8">
          <h1 className="font-serif text-3xl text-primary">주문 상세</h1>
          <div className="text-sm text-muted-foreground font-mono mt-1">
            {order.orderNo}
          </div>
        </div>

        {/* Timeline */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="text-sm font-medium text-primary mb-4">
              제작 · 배송 진행 상태
            </div>
            <div className="flex justify-between gap-2 overflow-x-auto pb-2">
              {ORDER_STATUS_FLOW.map((s, idx) => {
                const reached = currentIdx >= idx;
                const isCurrent = currentIdx === idx;
                return (
                  <div
                    key={s}
                    className="flex-1 min-w-24 flex flex-col items-center text-center"
                  >
                    <div
                      className={
                        "w-7 h-7 rounded-full border flex items-center justify-center text-xs mb-1 " +
                        (isCurrent
                          ? "bg-primary border-primary text-primary-foreground"
                          : reached
                            ? "bg-accent border-accent text-accent-foreground"
                            : "bg-background border-border text-muted-foreground")
                      }
                    >
                      {idx + 1}
                    </div>
                    <div
                      className={
                        "text-xs " +
                        (reached ? "text-primary" : "text-muted-foreground")
                      }
                    >
                      {ORDER_STATUS_LABEL[s]}
                    </div>
                  </div>
                );
              })}
            </div>
            {order.trackingNumber && (
              <div className="mt-4 text-sm text-muted-foreground border-t border-border/60 pt-3">
                운송장: {order.trackingCarrier} · {order.trackingNumber}
              </div>
            )}
            {order.memo && (
              <div className="mt-2 text-xs text-muted-foreground">
                관리자 메모: {order.memo}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            <Card>
              <CardContent className="p-4">
                <img
                  src={order.imageUrl}
                  alt="작품"
                  className="w-full aspect-square object-cover rounded-sm"
                />
              </CardContent>
            </Card>
          </div>
          <div className="md:col-span-2 space-y-6">
            <Card>
              <CardContent className="p-5">
                <div className="font-serif text-lg text-primary mb-3">
                  주문 항목
                </div>
                <div className="space-y-1 text-sm">
                  {items.map((i) => (
                    <div key={i.id} className="flex justify-between">
                      <span className="text-muted-foreground">
                        {i.productName}
                      </span>
                      <span>{formatKRW(i.unitPrice)}</span>
                    </div>
                  ))}
                  <div className="border-t border-border/60 mt-2 pt-2 flex justify-between text-base">
                    <span className="font-medium text-primary">합계</span>
                    <span className="font-serif text-xl text-primary">
                      {formatKRW(order.totalAmount)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {shipping && (
              <Card>
                <CardContent className="p-5">
                  <div className="font-serif text-lg text-primary mb-2">
                    배송지
                  </div>
                  <div className="text-sm">
                    {shipping.recipientName} · {shipping.phone}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    [{shipping.postalCode}] {shipping.address1}{" "}
                    {shipping.address2}
                  </div>
                  {shipping.note && (
                    <div className="text-xs text-muted-foreground mt-2">
                      요청사항: {shipping.note}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardContent className="p-5">
                <div className="font-serif text-lg text-primary mb-2">
                  결제 내역
                </div>
                <div className="text-sm space-y-1">
                  {payments.map((p) => (
                    <div key={p.id} className="flex justify-between">
                      <span className="text-muted-foreground">
                        {p.provider} · {p.status}
                      </span>
                      <span>{formatKRW(p.amount)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </SitePageShell>
  );
}
